import type { Key, KeyAction } from '@sprinters/shared';

const KEY_BY_CODE: Readonly<Record<string, Key>> = {
  ArrowLeft: 'left',
  ArrowRight: 'right',
};

export type InputSender = (key: Key, action: KeyAction, clientTimeMs: number) => void;

/** Capte les flèches gauche/droite et les transmet, horodatées avec l'horloge locale. */
export class InputController {
  /** État des touches, lu par l'affichage pour éclairer les flèches. */
  readonly pressed: Record<Key, boolean> = { left: false, right: false };
  private enabled = false;

  constructor(private readonly send: InputSender) {
    window.addEventListener('keydown', this.onKeyDown);
    window.addEventListener('keyup', this.onKeyUp);
    window.addEventListener('blur', this.releaseAll);
    document.addEventListener('visibilitychange', this.onVisibilityChange);
  }

  setEnabled(enabled: boolean): void {
    if (this.enabled === enabled) return;
    this.enabled = enabled;
    if (!enabled) this.releaseAll();
  }

  destroy(): void {
    this.releaseAll();
    window.removeEventListener('keydown', this.onKeyDown);
    window.removeEventListener('keyup', this.onKeyUp);
    window.removeEventListener('blur', this.releaseAll);
    document.removeEventListener('visibilitychange', this.onVisibilityChange);
  }

  private readonly onKeyDown = (event: KeyboardEvent): void => {
    const key = KEY_BY_CODE[event.code];
    if (!key || !this.enabled) return;
    event.preventDefault();
    if (event.repeat || this.pressed[key]) return;
    this.pressed[key] = true;
    this.send(key, 'down', performance.now());
  };

  private readonly onKeyUp = (event: KeyboardEvent): void => {
    const key = KEY_BY_CODE[event.code];
    if (!key || !this.pressed[key]) return;
    event.preventDefault();
    this.pressed[key] = false;
    this.send(key, 'up', performance.now());
  };

  /** Relâche les touches enfoncées (perte de focus, fin de course...) pour éviter un état bloqué. */
  private readonly releaseAll = (): void => {
    for (const key of ['left', 'right'] as const) {
      if (!this.pressed[key]) continue;
      this.pressed[key] = false;
      this.send(key, 'up', performance.now());
    }
  };

  private readonly onVisibilityChange = (): void => {
    if (document.hidden) this.releaseAll();
  };
}
